import React, { useState, useEffect, useRef } from "react";
import { Printer, X, Plus, Trash2, PenLine, Download } from "lucide-react";
import { CABINET, ATTEST_TYPES } from "@/data/fiche";
import { downloadElementAsPdf } from "@/lib/pdf";

const SIG_KEY = "m2hat_signature";

const fmt = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

const DateList = ({ label, dates, onChange, T, prefix }) => {
  const set = (i, v) => onChange(dates.map((d, idx) => (idx === i ? v : d)));
  const add = () => onChange([...dates, ""]);
  const remove = (i) => onChange(dates.filter((_, idx) => idx !== i));
  return (
    <div>
      <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{label}</label>
      <div className="space-y-2">
        {dates.map((d, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="date" value={d} onChange={(e) => set(i, e.target.value)} data-testid={`${prefix}-${i}`}
              className="flex-1 rounded-xl border border-[var(--border)] bg-white/80 px-3 py-2 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
            <button onClick={() => remove(i)} data-testid={`${prefix}-remove-${i}`} className="text-[#B0553B] hover:opacity-70" aria-label={T.removeDate}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <button onClick={add} data-testid={`${prefix}-add`} className="mt-2 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-all">
        <Plus size={14} /> {T.addDate}
      </button>
    </div>
  );
};

// Attestation — champs éditables, dates multiples, en-tête bannière M2HAT.
// Aucun archivage : uniquement génération PDF (impression navigateur).
export const Attestation = ({ nom: initNom, prenom: initPrenom, lang, T, onClose }) => {
  const [typeKey, setTypeKey] = useState("consultation");
  const [nom, setNom] = useState(initNom || "");
  const [prenom, setPrenom] = useState(initPrenom || "");
  const [dob, setDob] = useState("");
  const [datesReal, setDatesReal] = useState([new Date().toISOString().slice(0, 10)]);
  const [datesPlanned, setDatesPlanned] = useState([]);
  const [signature, setSignature] = useState("");
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString().slice(0, 10));
  const docRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const type = ATTEST_TYPES.find((x) => x.key === typeKey) || ATTEST_TYPES[0];

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const name = [nom, prenom].filter(Boolean).join("_") || "attestation";
      await downloadElementAsPdf(docRef.current, `attestation_${name}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    try { const s = localStorage.getItem(SIG_KEY); if (s) setSignature(s); } catch { /* ignore */ }
  }, []);

  const onSignatureFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result;
      setSignature(url);
      try { localStorage.setItem(SIG_KEY, url); } catch { /* ignore */ }
    };
    reader.readAsDataURL(file);
  };
  const removeSignature = () => {
    setSignature("");
    try { localStorage.removeItem(SIG_KEY); } catch { /* ignore */ }
  };

  const fullName = [nom, prenom].filter(Boolean).join(" ") || "…";
  const realList = datesReal.filter(Boolean);
  const plannedList = datesPlanned.filter(Boolean);

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
        <button onClick={onClose} data-testid="attest-close" className="inline-flex items-center gap-1.5 text-sm text-[var(--ink-soft)] hover:text-[var(--gold)]">
          <X size={16} /> {T.closeAttest}
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => window.print()} data-testid="attest-print" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-all">
            <Printer size={16} /> {T.printPdf}
          </button>
          <button onClick={downloadPdf} disabled={downloading} data-testid="attest-download" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60">
            <Download size={16} /> {T.downloadPdf}
          </button>
        </div>
      </div>

      <div className="rounded-2xl bg-[var(--sand)] border border-[var(--border)] p-5 mb-6 no-print space-y-4" data-testid="attest-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{T.attestType}</label>
            <select value={typeKey} onChange={(e) => setTypeKey(e.target.value)} data-testid="attest-type"
              className="w-full rounded-xl border border-[var(--border)] bg-white/80 px-3.5 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]">
              {ATTEST_TYPES.map((x) => (<option key={x.key} value={x.key}>{x[lang]}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{T.dob}</label>
            <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-testid="attest-dob"
              className="w-full rounded-xl border border-[var(--border)] bg-white/80 px-3.5 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{T.nom}</label>
            <input value={nom} onChange={(e) => setNom(e.target.value)} data-testid="attest-nom"
              className="w-full rounded-xl border border-[var(--border)] bg-white/80 px-3.5 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{T.prenom}</label>
            <input value={prenom} onChange={(e) => setPrenom(e.target.value)} data-testid="attest-prenom"
              className="w-full rounded-xl border border-[var(--border)] bg-white/80 px-3.5 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DateList label={T.datesRealisees} dates={datesReal} onChange={setDatesReal} T={T} prefix="attest-date-real" />
          <DateList label={T.plannedDates} dates={datesPlanned} onChange={setDatesPlanned} T={T} prefix="attest-date-planned" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{T.signatureLabel}</label>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-all cursor-pointer">
              <PenLine size={14} /> {T.signatureAdd}
              <input type="file" accept="image/*" onChange={onSignatureFile} data-testid="attest-signature-input" className="hidden" />
            </label>
            {signature && (
              <>
                <img src={signature} alt="signature" className="h-12 object-contain" data-testid="attest-signature-preview" />
                <button onClick={removeSignature} data-testid="attest-signature-remove" className="text-[#B0553B] hover:opacity-70" aria-label={T.signatureRemove}><Trash2 size={16} /></button>
              </>
            )}
          </div>
          <p className="text-xs text-[var(--ink-soft)] mt-1.5">{T.signatureHint}</p>
        </div>
        <div className="sm:w-1/2">
          <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{T.signatureDate}</label>
          <input type="date" value={signatureDate} onChange={(e) => setSignatureDate(e.target.value)} data-testid="attest-signature-date"
            className="w-full rounded-xl border border-[var(--border)] bg-white/80 px-3.5 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
        </div>
      </div>

      <div ref={docRef} className="print-container attest-a4-page mx-auto max-w-2xl bg-white text-[var(--ink)]" data-testid="attest-document">
        <div className="relative">
          <img src="/attestation-header.png" alt={CABINET.name} className="w-full block" data-testid="attest-header-img" />
          <div className="h-[3px] w-full" style={{ backgroundColor: "var(--gold)" }} />
        </div>

        <div className="px-10 sm:px-14 py-10">
          <div className="mb-12">
            <div className="text-left text-[11px] leading-relaxed text-[var(--ink-soft)] space-y-0.5">
              <p className="font-semibold text-[var(--ink)] tracking-wide">{CABINET.cabinet}</p>
              <p>{CABINET.mail}</p>
              <p>SIREN {CABINET.siren}</p>
              <p>RPPS {CABINET.rpps}</p>
            </div>
          </div>

          <h2 className="text-center font-serif-title text-3xl tracking-wide mb-2" style={{ color: "var(--gold)" }} data-testid="attest-title">{type.title}</h2>
          <div className="mx-auto mb-12 h-px w-16" style={{ backgroundColor: "var(--gold)" }} />

          <p className="text-[15px] leading-loose text-justify mb-8">
            {T.attestSigned} <span className="font-semibold">{CABINET.praticien}</span>, {CABINET.titre}, {type.body}
          </p>
          <p className="text-[15px] mb-10 pl-5 border-l-2 font-medium" style={{ borderColor: "var(--gold)" }} data-testid="attest-patient">
            {fullName}{dob ? `, ${T.bornLe} ${fmt(dob)}` : ""}
          </p>

          <p className="text-xs uppercase tracking-[0.14em] text-[var(--ink-soft)] mb-2">{T.datesRealiseesPrinted}</p>
          <ul className="mb-8 space-y-1.5" data-testid="attest-dates-real">
            {(realList.length ? realList : [""]).map((d, i) => (
              <li key={i} className="flex items-center gap-2.5 text-[15px]">
                <span className="inline-block h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: "var(--gold)" }} />
                {d ? fmt(d) : "…"}
              </li>
            ))}
          </ul>

          {plannedList.length > 0 && (
            <>
              <p className="text-xs uppercase tracking-[0.14em] text-[var(--ink-soft)] mb-2">{T.plannedDatesPrinted}</p>
              <ul className="mb-8 space-y-1.5" data-testid="attest-dates-planned">
                {plannedList.map((d, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-[15px]">
                    <span className="inline-block h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: "var(--gold-light)" }} />
                    {fmt(d)}
                  </li>
                ))}
              </ul>
            </>
          )}

          <p className="text-[15px] leading-loose text-justify mt-10 mb-16">{T.attestLegal}</p>

          <div className="flex flex-col items-end text-right text-[15px]">
            <p className="text-[var(--ink-soft)]">{T.attestRequest}</p>
            <p className="mt-6">{T.signedOn} {fmt(signatureDate)}</p>
            <p className="mt-1 font-medium">{T.attestSignature}</p>
            {signature && <img src={signature} alt="signature" className="mt-3 max-h-20 object-contain" style={{ mixBlendMode: "multiply" }} data-testid="attest-signature-doc" />}
          </div>
        </div>
      </div>
    </div>
  );
};
